import { useState } from 'react'
import { BarChart2 } from 'lucide-react'
import {
  analyticsEventLabel,
  displayPersonName,
  formatRelativeTime,
  useAnalyticsQuoteSummary,
} from 'src/api/db/apiAnalytics'
import { AnalyticsActivityChart } from 'src/shared/components/AnalyticsActivityChart'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from 'src/shared/components/ui/dialog'
import { Skeleton } from 'src/shared/components/ui/skeleton'
import { useLocalization } from 'src/shared/hooks/useLocalization'

const Kpi = ({
  label,
  value,
}: {
  label: string
  value: string | number
}) => (
  <Column className="min-w-0 gap-0.5 rounded-xl border border-[hsl(var(--border-subtle))] bg-background p-3">
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="truncate text-lg font-semibold tabular-nums">{value}</p>
  </Column>
)

export const RequestQuoteAnalyticsDialog = ({
  quoteId,
}: {
  quoteId: number
}) => {
  const { t, locale } = useLocalization()
  const [open, setOpen] = useState(false)
  const { data, isPending, isError } = useAnalyticsQuoteSummary(quoteId, open)
  const lang = locale === 'sv' ? 'sv' : 'en'

  const hasActivity = Boolean(data?.last_activity_at)

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="h-9 w-9 shrink-0 rounded-full border-gray-200/80 bg-white/90 shadow-lg backdrop-blur-md"
        onClick={() => setOpen(true)}
        aria-label={t('Request activity', 'Aktivitet för förfrågan')}
      >
        <BarChart2 className="h-3.5 w-3.5" />
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[min(90vh,820px)] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {t('Request activity', 'Aktivitet för förfrågan')}
            </DialogTitle>
            <p className="text-sm text-muted-foreground">
              {t(
                'How customers have opened and used this request, including the share link.',
                'Hur kunder har öppnat och använt den här förfrågan, inklusive dellänken.'
              )}
            </p>
          </DialogHeader>

          {isPending ? (
            <Column className="gap-3 pt-2">
              <Row className="gap-2">
                <Skeleton className="h-16 flex-1" />
                <Skeleton className="h-16 flex-1" />
                <Skeleton className="h-16 flex-1" />
              </Row>
              <Skeleton className="h-56 w-full" />
            </Column>
          ) : isError ? (
            <p className="pt-2 text-sm text-muted-foreground">
              {t(
                'Could not load activity for this request.',
                'Kunde inte ladda aktivitet för den här förfrågan.'
              )}
            </p>
          ) : !hasActivity ? (
            <p className="pt-2 text-sm text-muted-foreground">
              {t(
                'No customer activity yet. Share the link or wait for them to open the request.',
                'Ingen kundaktivitet ännu. Dela länken eller vänta tills de öppnar förfrågan.'
              )}
            </p>
          ) : (
            <Column className="gap-5 pt-2">
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <Kpi
                  label={t('Share opens', 'Delöppningar')}
                  value={data.share_opens}
                />
                <Kpi
                  label={t('Unique visitors', 'Unika besökare')}
                  value={data.unique_visitors}
                />
                <Kpi
                  label={t('In-app opens', 'Öppningar i appen')}
                  value={data.request_opens}
                />
                <Kpi
                  label={t('Messages sent', 'Skickade meddelanden')}
                  value={data.chat_sent ?? 0}
                />
                <Kpi
                  label={t('Catalog opens', 'Katalogöppningar')}
                  value={data.catalog_open ?? 0}
                />
                <Kpi
                  label={t('Checkouts opened', 'Kassor öppnade')}
                  value={data.checkout_open ?? 0}
                />
                <Kpi
                  label={t('Product changes', 'Produktändringar')}
                  value={data.item_change ?? 0}
                />
                <Kpi
                  label={t('Last activity', 'Senaste aktivitet')}
                  value={formatRelativeTime(data.last_activity_at, lang)}
                />
              </div>

              <Column className="gap-2">
                <p className="text-sm font-medium">
                  {t('Last 90 days', 'Senaste 90 dagarna')}
                </p>
                <AnalyticsActivityChart
                  locale={locale}
                  data={data.daily.map((row) => ({
                    day: row.day,
                    share: row.share,
                    in_app: row.in_app,
                    engagement: row.engagement,
                  }))}
                  series={[
                    {
                      key: 'share',
                      label: t('Share link', 'Dellänk'),
                      color: '#38bdf8',
                    },
                    {
                      key: 'in_app',
                      label: t('In app', 'I appen'),
                      color: '#818cf8',
                    },
                    {
                      key: 'engagement',
                      label: t('Engagement', 'Engagemang'),
                      color: '#fb923c',
                    },
                  ]}
                />
              </Column>

              {data.recent.length > 0 ? (
                <Column className="gap-2">
                  <p className="text-sm font-medium">
                    {t('Recent activity', 'Senaste aktiviteten')}
                  </p>
                  <Column className="gap-0 overflow-hidden rounded-xl border border-[hsl(var(--border-subtle))]">
                    {data.recent.map((event, index) => (
                      <Row
                        key={`${event.created_at}-${event.name}-${index}`}
                        className="items-start justify-between gap-3 border-b border-[hsl(var(--border-subtle))] px-3 py-2 last:border-b-0"
                      >
                        <Column className="min-w-0 gap-0.5">
                          <p className="text-sm">
                            {analyticsEventLabel(event.name, t)}
                          </p>
                          <p className="truncate text-xs text-muted-foreground">
                            {displayPersonName({
                              firstName: event.user_first_name,
                              lastName: event.user_last_name,
                              email: event.user_email,
                              fallback: t(
                                'Anonymous visitor',
                                'Anonym besökare'
                              ),
                            })}
                          </p>
                        </Column>
                        <p className="shrink-0 text-xs text-muted-foreground">
                          {formatRelativeTime(event.created_at, lang)}
                        </p>
                      </Row>
                    ))}
                  </Column>
                </Column>
              ) : null}
            </Column>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
